// Sugestão de vínculo entre a conversa e o cadastro (Fase 15).
//
// Responde "de quem é esta conversa?" — e responde com uma LISTA, nunca
// vinculando sozinho. O motivo está nos números do cadastro, medidos em
// produção: um mesmo e-mail aparece em 133 unidades consumidoras e um mesmo
// telefone em 50. Vincular automático acertaria o caso raro e erraria no de
// maior volume.
//
// ⚠️ E não é hipótese. Hoje, em produção, a conversa de "Marilia Catarinne
// Oliveira Alves" aponta para a unidade de "Renato da Silva Sales" — o telefone
// dela está no cadastro dele, e o vínculo foi criado antes de existir
// conferência. Esta tela existe para que o próximo não aconteça, e para que
// este apareça.
//
// A REGRA DE OURO DESTE COMPONENTE: nada aqui decide. O que ele faz é dar à
// pessoa o que ela precisa para decidir — o nome do outro lado, por que casou,
// e se aquele telefone é de mais alguém.

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Link2, Loader2, Unlink, Users } from 'lucide-react';
import { buscarSugestoesVinculo, confirmarVinculo, type CandidatoVinculo } from '../apiVinculo';

interface Props {
  contactId: string;
  /** O que já está gravado, para a tela saber o que é confirmação e o que é troca. */
  unidadeAtual: number | null;
  negociacaoAtual: number | null;
  onVinculado: () => void;
}

const ROTULO: Record<CandidatoVinculo['tipo'], string> = {
  unidade_consumidora: 'Unidade consumidora',
  negociacao: 'Negociação',
};

export default function SugestaoVinculo({
  contactId,
  unidadeAtual,
  negociacaoAtual,
  onVinculado,
}: Props) {
  const [candidatos, setCandidatos] = useState<CandidatoVinculo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [aberto, setAberto] = useState(false);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await buscarSugestoesVinculo(contactId);
      setCandidatos(r.candidatos);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [contactId]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  const jaVinculado = (c: CandidatoVinculo) =>
    c.tipo === 'unidade_consumidora'
      ? String(unidadeAtual ?? '') === c.id
      : String(negociacaoAtual ?? '') === c.id;

  const aplicar = async (c: CandidatoVinculo | null, tipo: CandidatoVinculo['tipo']) => {
    setSalvando(c ? `${c.tipo}:${c.id}` : `limpar:${tipo}`);
    setErro(null);
    try {
      await confirmarVinculo(contactId, tipo, c ? Number(c.id) : null);
      onVinculado();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(null);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-[11px] text-gray-400">
        <Loader2 size={12} className="animate-spin" />
        Procurando no cadastro...
      </div>
    );
  }

  // Sem candidato e sem vínculo é o caso mais comum: só 35% das unidades têm
  // telefone cadastrado. Uma caixa vazia gritando "nenhum resultado" em toda
  // conversa vira ruído — some em silêncio.
  if (!candidatos.length && !unidadeAtual && !negociacaoAtual && !erro) return null;

  const visiveis = aberto ? candidatos : candidatos.slice(0, 2);

  return (
    <div className="border-t border-gray-200 px-4 py-3.5 dark:border-slate-700">
      <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        <Link2 size={12} />
        Cadastro
      </h4>

      {erro && (
        <p className="mb-2 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {erro}
        </p>
      )}

      <ul className="space-y-1.5">
        {visiveis.map((c) => {
          const atual = jaVinculado(c);
          const chave = `${c.tipo}:${c.id}`;
          return (
            <li
              key={chave}
              className={`rounded-lg border p-2 ${
                atual
                  ? 'border-primary/40 bg-primary/5'
                  : 'border-gray-200 dark:border-slate-700'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-gray-800 dark:text-slate-200">
                    {c.nome ?? `#${c.id}`}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {ROTULO[c.tipo]} #{c.id}
                  </p>
                </div>
                {atual ? (
                  <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    vinculado
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={salvando !== null}
                    onClick={() => void aplicar(c, c.tipo)}
                    className="shrink-0 rounded bg-primary px-2 py-1 text-[10px] font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    {salvando === chave ? '...' : 'Vincular'}
                  </button>
                )}
              </div>

              {/* O motivo do palpite. Sem ele a pessoa confirma no escuro — e
                  confirmar no escuro é como a conversa da Marilia foi parar no
                  cadastro do Renato. */}
              <p className="mt-1 text-[10px] leading-snug text-gray-500 dark:text-slate-400">
                {c.motivo}
              </p>

              {/* Os dois avisos que mudam a decisão. */}
              {c.nomeConfere === 'diverge' && (
                <p className="mt-1 flex items-start gap-1 text-[10px] leading-snug text-amber-700 dark:text-amber-400">
                  <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                  O nome do cadastro é diferente do nome do contato. Pode ser o telefone de outra
                  pessoa — confira antes de vincular.
                </p>
              )}
              {c.compartilhadoCom > 1 && (
                <p className="mt-1 flex items-start gap-1 text-[10px] leading-snug text-gray-500 dark:text-slate-400">
                  <Users size={10} className="mt-0.5 shrink-0" />
                  Este contato aparece em {c.compartilhadoCom} cadastros — não identifica ninguém
                  sozinho.
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {candidatos.length > 2 && (
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="mt-1.5 text-[10px] font-medium text-primary hover:underline"
        >
          {aberto ? 'mostrar menos' : `ver os outros ${candidatos.length - 2}`}
        </button>
      )}

      {/* Desfazer precisa ser tão fácil quanto vincular: o erro que esta tela
          previne já existe no banco, e alguém vai precisar corrigir. */}
      {(unidadeAtual || negociacaoAtual) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {unidadeAtual && (
            <button
              type="button"
              disabled={salvando !== null}
              onClick={() => void aplicar(null, 'unidade_consumidora')}
              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-red-600 disabled:opacity-50 dark:text-slate-400"
            >
              <Unlink size={10} /> desvincular unidade
            </button>
          )}
          {negociacaoAtual && (
            <button
              type="button"
              disabled={salvando !== null}
              onClick={() => void aplicar(null, 'negociacao')}
              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-red-600 disabled:opacity-50 dark:text-slate-400"
            >
              <Unlink size={10} /> desvincular negociação
            </button>
          )}
        </div>
      )}

      {!candidatos.length && (unidadeAtual || negociacaoAtual) && (
        <p className="mt-1 flex items-start gap-1 text-[10px] leading-snug text-gray-500 dark:text-slate-400">
          <Check size={10} className="mt-0.5 shrink-0" />
          O cadastro atual não casa com o telefone nem com o e-mail deste contato. Pode ter sido
          vinculado à mão — ou por engano.
        </p>
      )}
    </div>
  );
}
